/** @jsxImportSource @emotion/react */
import { useTranslation } from "react-i18next"
import { withMyTheme } from "../../theme/theme"
import { css } from "@emotion/react"
import { mobileCss } from "../../theme/isMobile"
import { TextSizes } from "../../util/textSizes"
import { MainConfig } from "../../../mainConfig/configHelper"
import { resolveIcon } from "../menu/menuIconResolver"
import { StayTunedPlaceholder } from "../../components/StayTunedPlaceholder"

export const SCHEDULE_ID = "schedule"

const ScheduleContentStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 2vh;
    justify-content: center;
    height: 100vh;
    background: ${theme.palette.background.default};
    color: ${theme.palette.text.primary};
    ${mobileCss(`
        padding-top: 5vh;
        height: auto;
        width: 100vw;
    `)}
`)

const ScheduleTitleStyle = withMyTheme((theme) => css`
    font-size: ${TextSizes.PAGE_TITLE};
    font-family: ${theme.typography.h1.fontFamily};
    color: ${theme.palette.primary.main};
    margin-bottom: 2rem;
    text-align: center;
    ${mobileCss(`
        font-size: clamp(2rem, 8vw, 3rem);
        margin-bottom: 1.5rem;
    `)}
`)

const ScheduleDescriptionStyle = withMyTheme((theme) => css`
    font-size: ${TextSizes.DESCRIPTION};
    font-family: ${theme.typography.body1.fontFamily};
    color: ${theme.palette.text.primary};
    text-align: center;
    max-width: 75vw;
    line-height: 1.6;
    margin-bottom: 3rem;
    ${mobileCss(`
        font-size: 1.1rem;
        margin-bottom: 2rem;
        max-width: 85vw;
    `)}
`)

const ScheduleGridStyle = withMyTheme(() => css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: repeat(5, 1fr);
    grid-auto-flow: column;
    gap: 3vh;
    max-width: 50vw;
    width: 100%;
    ${mobileCss(`
        grid-template-columns: 1fr;
        grid-template-rows: repeat(10, 1fr);
        grid-auto-flow: row;
        gap: 1.5rem;
        max-width: none;
        width: 85vw;
        margin-bottom: 5vh;
        padding-left: 5vw;
        padding-right: 5vw;
    `)}
`)

const ScheduleItemStyle = withMyTheme((theme) => css`
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 2vh;
    background: ${theme.palette.background.paper};
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    ${mobileCss(`
        gap: 0.8rem;
        padding: 0.8rem;
    `)}
`)

const ScheduleIconStyle = withMyTheme((theme) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 6vh;
    height: 6vh;
    border-radius: 50%;
    background: ${theme.palette.secondary.light};
    color: ${theme.palette.primary.dark};
    flex-shrink: 0;
    ${mobileCss(`
        width: 40px;
        height: 40px;
    `)}
`)

const ScheduleContentItemStyle = withMyTheme(() => css`
    display: flex;
    flex-direction: column;
    flex: 1;
`)

const ScheduleTimeStyle = withMyTheme((theme) => css`
    font-size: 2.8vh;
    font-weight: 700;
    color: ${theme.palette.primary.main};
    font-family: ${theme.typography.body1.fontFamily};
    ${mobileCss(`
        font-size: 1rem;
    `)}
`)

const ScheduleEventStyle = withMyTheme((theme) => css`
    font-size: 2vh;
    color: ${theme.palette.text.primary};
    font-family: ${theme.typography.body1.fontFamily};
    margin-top: 0.5vh;
    ${mobileCss(`
        font-size: 0.9rem;
    `)}
`)

const ScheduleIconFontSizeStyle = withMyTheme(() => css`
    font-size: 3vh;
`)

interface ScheduleItemProps {
    time: string;
    event: string;
    icon: React.ReactNode;
}

const ScheduleItem = ({ time, event, icon }: ScheduleItemProps) => (
    <div css={ScheduleItemStyle}>
        <div css={ScheduleIconStyle}>
            {icon}
        </div>
        <div css={ScheduleContentItemStyle}>
            <div css={ScheduleTimeStyle}>{time}</div>
            <div css={ScheduleEventStyle}>{event}</div>
        </div>
    </div>
)

export const Schedule = () => {
    const { t } = useTranslation()

    const events = MainConfig.schedule?.events ?? []
    const isStayTuned = MainConfig.schedule?.stayTuned === true

    const dynamicGridRows = Math.ceil(events.length / 2)

    const dynamicGridStyle = css`
        grid-template-rows: repeat(${dynamicGridRows}, 1fr);
        ${mobileCss(`
            grid-template-rows: repeat(${events.length}, 1fr);
        `)}
    `

    const SCHEDULE_EMOJIS = ['💒', '💍', '🎶', '🥳', '✨']

    return (
        <section id={SCHEDULE_ID} css={ScheduleContentStyle}>
            <div css={ScheduleTitleStyle}>
                {t('schedule.title')}
            </div>

            {isStayTuned ? (
                <StayTunedPlaceholder
                    message={t('schedule.stayTunedMessage')}
                    subtext={t('schedule.stayTunedSubtext')}
                    emojis={SCHEDULE_EMOJIS}
                />
            ) : (
                <>
                    <div css={ScheduleDescriptionStyle}>
                        {t('schedule.description')}
                    </div>
                    <div css={[ScheduleGridStyle, dynamicGridStyle]}>
                        {events.map((event) => (
                            <ScheduleItem
                                key={event.id}
                                time={event.time}
                                event={event.title}
                                icon={resolveIcon(event.iconType, event.iconValue, ScheduleIconFontSizeStyle)}
                            />
                        ))}
                    </div>
                </>
            )}
        </section>
    )
}
